// src/components/admin/ViewMusicContentModal.js
import React, { useEffect, useState } from "react";
import {
    X,
    Music,
    BadgeCheck,
    Clock,
    Info,
    Loader2,
    Music2,
    FileText,
    Download,
    ChevronDown,
    FileAudio,
    List
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNotification } from "../../context/NotificationContext";
import { useMusicContent } from "../../services/admin/MusicContentServices";

export default function ViewMusicContentModal({ itemId, isOpen, onClose }) {
    const { fetchMusicProductById, downloadMusicFile, loading: serviceLoading } = useMusicContent();
    const { showNotification } = useNotification();
    const [item, setItem] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [activeQualityDropdown, setActiveQualityDropdown] = useState(false);

    const qualityOptions = [
        { label: "Original Format", value: "original", desc: "The source file uploaded", icon: <Music size={14} /> },
        { label: "High Quality MP3", value: "mp3_high", desc: "Crystal clear 320kbps", icon: <FileAudio size={14} /> },
        { label: "Standard MP3", value: "mp3_standard", desc: "Smaller size 128kbps", icon: <FileAudio size={14} /> },
        { label: "WAV Lossless", value: "wav", desc: "Studio quality uncompressed", icon: <FileAudio size={14} /> },
    ];

    useEffect(() => {
        if (isOpen && itemId) {
            const loadItem = async () => {
                const data = await fetchMusicProductById(itemId);
                setItem(data);
            };
            loadItem();
            setActiveQualityDropdown(false);
        } else if (!isOpen) {
            setItem(null);
            setActiveQualityDropdown(false);
        }
    }, [isOpen, itemId, fetchMusicProductById]);

    if (!isOpen) return null;

    const getStatusStyle = (status) => {
        return status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    };

    const handleDownload = async (mediaType = "audio", quality = "original") => {
        if (downloading) return;
        setActiveQualityDropdown(false);

        setDownloading(true);
        try {
            const response = await downloadMusicFile(item.id, mediaType, quality);

            // Extract filename from Content-Disposition if available
            let filename = `${item.name.replace(/\s+/g, '_')}_${mediaType === 'audio' ? 'music' : 'content'}`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?($|;)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            showNotification({
                type: "success",
                title: "Download Started",
                message: `${item.name} is being saved to your device.`
            });
        } catch (error) {
            console.error("Download error:", error);
            // Error notification is handled in the service
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl overflow-y-auto transform transition-all border border-black/5 dark:border-white/5">
                <div className="sticky top-0 z-10 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                    <h2 className="text-xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                        Music Product Details
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-black hover:text-black/70 dark:text-white/60 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {serviceLoading || !item ? (
                    <div className="p-20 flex flex-col items-center justify-center gap-4">
                        <Loader2 className="animate-spin text-jazz-brown" size={48} />
                        <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Fetching details...</p>
                    </div>
                ) : (
                    <div className="p-6 sm:p-8 animate-in fade-in duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                            <div className="md:col-span-5 space-y-6">
                                <div className="aspect-square w-full rounded-2xl bg-[#F5F5F5] dark:bg-zinc-800 border border-black/5 overflow-hidden shadow-lg shadow-black/5">
                                    {item.image ? (
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Music2 size={48} className="text-black/10" />
                                        </div>
                                    )}
                                </div>

                                <div className="bg-jazz-brown/5 p-4 rounded-xl border border-jazz-brown/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Product Info</span>
                                        <Music size={14} className="text-jazz-brown" />
                                    </div>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Price</span>
                                            <span className="text-black dark:text-white font-bold">${parseFloat(item.price).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Category</span>
                                            <span className="text-jazz-brown font-bold uppercase">{item.category_name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Status</span>
                                            <span className="text-black dark:text-white/60 capitalize">{item.status}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-7 space-y-8">
                                <div className="space-y-4">
                                    <div className="flex flex-wrap items-center gap-3">
                                        {item.is_popular && (
                                            <span className="px-2 py-1 bg-jazz-brown text-white rounded text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-1">
                                                <BadgeCheck size={10} /> Popular
                                            </span>
                                        )}
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                                            {item.status === 'active' ? <Clock size={12} /> : <X size={12} />}
                                            {item.status}
                                        </span>
                                    </div>

                                    <h1 className="text-4xl font-bold text-black dark:text-white leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                                        {item.name}
                                    </h1>
                                </div>

                                <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                    <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3">Product Description</h3>
                                    <div className="text-black dark:text-white/70 text-base leading-relaxed whitespace-pre-wrap">
                                        {item.description}
                                    </div>
                                </div>

                                {item.category_name.toLowerCase().includes('album') && item.track_list && item.track_list.length > 0 && (
                                    <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <List size={12} /> Track Listing
                                        </h3>
                                        <div className="space-y-1 bg-gray-50 dark:bg-white/5 p-4 rounded-2xl">
                                            {item.track_list.map((track, index) => (
                                                <div key={index} className="flex items-center gap-3 py-2 border-b border-black/5 last:border-0 dark:border-white/5">
                                                    <span className="text-[10px] font-black text-jazz-brown w-4">{index + 1}.</span>
                                                    <span className="text-sm font-medium text-black dark:text-white uppercase tracking-tight">{track}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {item.audio && (
                                    <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <Music2 size={12} /> Preview Audio
                                        </h3>
                                        <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-2xl flex flex-col gap-4">
                                            <audio controls src={item.audio} className="w-full accent-jazz-brown" />

                                            <div className="relative">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => setActiveQualityDropdown(!activeQualityDropdown)}
                                                        disabled={downloading}
                                                        className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 bg-black text-white rounded-xl text-xs font-bold tracking-[1px] uppercase transition-all shadow-lg disabled:opacity-50 hover:bg-jazz-brown active:scale-95"
                                                    >
                                                        {downloading ? (
                                                            <Loader2 size={14} className="animate-spin" />
                                                        ) : (
                                                            <Download size={14} />
                                                        )}
                                                        {downloading ? 'Processing...' : 'Download Full Audio'}
                                                    </button>
                                                    <button
                                                        onClick={() => setActiveQualityDropdown(!activeQualityDropdown)}
                                                        className="py-3 px-3 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors border-l border-white/10"
                                                    >
                                                        <ChevronDown size={16} className={`transition-transform duration-200 ${activeQualityDropdown ? 'rotate-180' : ''}`} />
                                                    </button>
                                                </div>

                                                <AnimatePresence>
                                                    {activeQualityDropdown && (
                                                        <>
                                                            <div className="fixed inset-0 z-10" onClick={() => setActiveQualityDropdown(false)} />
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                className="absolute bottom-full left-0 mb-2 w-72 bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl border border-black/5 dark:border-white/5 z-20 overflow-hidden"
                                                            >
                                                                <div className="p-4 border-b border-black/5 dark:border-white/5 bg-gray-50/50 dark:bg-white/5">
                                                                    <p className="text-xs uppercase tracking-[2px] font-black text-jazz-brown">Select Quality</p>
                                                                </div>
                                                                <div className="py-2">
                                                                    {qualityOptions.map((opt) => (
                                                                        <button
                                                                            key={opt.value}
                                                                            onClick={() => handleDownload("audio", opt.value)}
                                                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 flex items-start gap-3 group transition-colors"
                                                                        >
                                                                            <div className="mt-1 text-black/60 dark:text-white/20 group-hover:text-jazz-brown transition-colors">
                                                                                {opt.icon}
                                                                            </div>
                                                                            <div>
                                                                                <p className="text-xs font-bold text-black dark:text-white group-hover:text-jazz-brown transition-colors">{opt.label}</p>
                                                                                <p className="text-xs text-black/70 dark:text-white/40 font-medium">{opt.desc}</p>
                                                                            </div>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </motion.div>
                                                        </>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {item.zip && (
                                    <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <Music2 size={12} /> Digital Archive (ZIP)
                                        </h3>
                                        <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-2xl">
                                            <button
                                                onClick={() => handleDownload("zip")}
                                                disabled={downloading}
                                                className="w-full flex items-center justify-between group transition-all disabled:opacity-50"
                                            >
                                                <div className="flex items-center gap-3 text-left">
                                                    <div className="p-2 bg-jazz-brown/10 rounded-lg text-jazz-brown group-hover:bg-jazz-brown group-hover:text-white transition-colors">
                                                        <Music2 size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-black dark:text-white capitalize">
                                                            {item.category_name.toLowerCase().includes('sheet') ? 'Sheet Music Archive' : 'Full Album Bundle'}
                                                        </p>
                                                        <p className="text-xs text-black/70 dark:text-white/50 uppercase font-bold tracking-tight">
                                                            {downloading ? 'Preparing download...' : 'Click to download ZIP archive'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="p-2 bg-white dark:bg-zinc-800 rounded-lg border border-black/5 text-black/70 dark:text-white/50 group-hover:text-jazz-brown group-hover:border-jazz-brown/30 transition-all">
                                                    {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {item.pdf && (
                                    <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <FileText size={12} /> Sheet Music (PDF)
                                        </h3>
                                        <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-2xl">
                                            <button
                                                onClick={() => handleDownload("pdf")}
                                                disabled={downloading}
                                                className="w-full flex items-center justify-between group transition-all disabled:opacity-50"
                                            >
                                                <div className="flex items-center gap-3 text-left">
                                                    <div className="p-2 bg-jazz-brown/10 rounded-lg text-jazz-brown group-hover:bg-jazz-brown group-hover:text-white transition-colors">
                                                        <FileText size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-black dark:text-white capitalize">Sheet Music File</p>
                                                        <p className="text-xs text-black/70 dark:text-white/50 uppercase font-bold tracking-tight">
                                                            {downloading ? 'Preparing download...' : 'Click to view or download'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="p-2 bg-white dark:bg-zinc-800 rounded-lg border border-black/5 text-black/70 dark:text-white/50 group-hover:text-jazz-brown group-hover:border-jazz-brown/30 transition-all">
                                                    {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-6 border-t border-black/5 dark:border-white/5 bg-gray-50 dark:bg-white/5 p-4 rounded-2xl">
                                    <div className="flex items-center gap-3">
                                        <Info size={18} className="text-jazz-brown" />
                                        <p className="text-xs text-black/70 dark:text-white/50 font-bold uppercase tracking-widest">
                                            This is a digital music product. No physical stock management is required for this item.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="sticky bottom-0 bg-white dark:bg-[#1A1A1A] p-6 border-t border-black/5 dark:border-white/5">
                    <button onClick={onClose} className="w-full py-4 bg-black text-white rounded-xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all uppercase shadow-lg">
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}
